import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useRouter } from 'expo-router';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';
import type { Project, Category, ProjectKeyword, ProjectCategoryInterest } from './database.client.types';
import { useSession } from './SessionContext';
import { isAnonymousUser } from '../utils/authHelpers';
import { activateDiscoveryProject } from './discoveryProjectService';
import { isProjectActivated } from './projectActivation';
import {
    getActivationRecoverySessionKey,
    getActivationRecoveryReason,
    shouldRecoverProjectActivation,
} from './projectActivationRecovery';
import { msg } from '@lingui/core/macro';
import type { LocalizedMessage } from './i18n/messages';

interface ProjectWithRelations extends Project {
    project_category_interests?: Array<ProjectCategoryInterest & {
        category?: Category;
    }>;
    project_keywords?: ProjectKeyword[];
}

interface ProjectContextType {
    project: ProjectWithRelations | null;
    isLoading: boolean;
    error: LocalizedMessage | null;
    isRecoveringActivation: boolean;
    isNewProject: boolean;
    isExistingProject: boolean;
    refetchProject: () => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

interface ProjectProviderProps {
    children: ReactNode;
    projectId: string;
    allowTransientNotFoundRetry?: boolean;
    deferProjectAccess?: boolean;
}

export function ProjectProvider({
    children,
    projectId,
    allowTransientNotFoundRetry = false,
    deferProjectAccess = false,
}: ProjectProviderProps) {
    const [project, setProject] = useState<ProjectWithRelations | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<LocalizedMessage | null>(null);
    const [isRecoveringActivation, setIsRecoveringActivation] = useState(false);
    const [projectActivatedState, setProjectActivatedState] = useState<boolean | null>(null);
    const { session, isLoading: sessionLoading } = useSession();
    const activationRecoverySessionKey = getActivationRecoverySessionKey(session);
    const router = useRouter();
    const transientRetryCountRef = useRef(0);
    const transientRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const activationRecoveryInFlightRef = useRef<Set<string>>(new Set());

    const clearTransientRetry = useCallback(() => {
        if (transientRetryTimeoutRef.current) {
            clearTimeout(transientRetryTimeoutRef.current);
            transientRetryTimeoutRef.current = null;
        }
    }, []);

    const scheduleTransientRetry = useCallback((reason: string, retryFn: () => Promise<void>) => {
        const maxRetries = 12;
        if (transientRetryCountRef.current >= maxRetries) {
            return false;
        }

        transientRetryCountRef.current += 1;
        const retryAttempt = transientRetryCountRef.current;
        clearTransientRetry();

        console.log(`🔍 [ProjectContext] Retrying fetch (${retryAttempt}/${maxRetries}) due to ${reason}`);
        transientRetryTimeoutRef.current = setTimeout(() => {
            retryFn();
        }, 350);

        return true;
    }, [clearTransientRetry]);

    const resetTransientRetry = useCallback(() => {
        transientRetryCountRef.current = 0;
        clearTransientRetry();
    }, [clearTransientRetry]);

    const handleRedirect = useCallback((currentSession: Session | null) => {
        console.log('🔍 [ProjectContext] Redirecting due to invalid project access');

        const redirectTarget = currentSession && !isAnonymousUser(currentSession)
            ? '/digests'
            : '/';

        console.log(`  - Redirecting to ${redirectTarget}`);
        router.replace(redirectTarget);
    }, [router]);

    const fetchProject = useCallback(async () => {
        // An exact activation must finalize before an unclaimed anonymous
        // project's rows become readable to the confirmed account. Its route
        // owns retries/navigation, including a slow or unavailable finalizer.
        if (deferProjectAccess) {
            clearTransientRetry();
            setProject(null);
            setError(null);
            setIsLoading(false);
            return;
        }
        if (sessionLoading) {
            return;
        }

        if (!projectId) {
            setError(msg({ id: 'projectContext.missingId', message: 'No project ID provided' }));
            setIsRecoveringActivation(false);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            console.log('🔍 [ProjectContext] Fetching project:', projectId);

            // Get session from supabase for discovery projects
            const { data: { session: localSession } } = await supabase.auth.getSession();
            const sessionCandidate = localSession || session;

            console.log('🔍 [ProjectContext] Current sessions:', {
                mainSession: session?.user?.id,
                localSession: localSession?.user?.id,
                isAnonymous: localSession ? isAnonymousUser(localSession) : false,
                hasLocalSession: !!localSession
            });

            if (!sessionCandidate) {
                resetTransientRetry();
                console.log('🔍 [ProjectContext] No session available, redirecting');
                setProject(null);
                setProjectActivatedState(null);
                setIsRecoveringActivation(false);
                setError(msg({ id: 'projectContext.noAccess', message: 'You do not have access to this project' }));
                handleRedirect(null);
                setIsLoading(false);
                return;
            }

            // For discovery projects, we need to use supabase
            // Fetch project with related categories and keywords
            const { data: projectData, error: fetchError } = await supabase
                .from('project')
                .select(`
                    *,
                    project_category_interests (
                        *,
                        category (*)
                    ),
                    project_keywords:project_keyword (*)
                `)
                .eq('id', projectId)
                .is('deleted_at', null)
                .single();

            if (fetchError) {
                console.error('🔍 [ProjectContext] Error fetching project:', {
                    error: fetchError,
                    code: fetchError.code,
                    message: fetchError.message,
                    details: fetchError.details,
                    hint: fetchError.hint,
                    projectId,
                    localSession: {
                        userId: localSession?.user?.id,
                        isAnonymous: localSession ? isAnonymousUser(localSession) : false,
                        hasEmail: !!localSession?.user?.email
                    },
                    mainSession: {
                        userId: session?.user?.id,
                        isAnonymous: session ? isAnonymousUser(session) : false,
                        hasEmail: !!session?.user?.email
                    }
                });

                // PGRST116 is the Supabase/PostgREST error code for "no rows returned"
                if (fetchError.code === 'PGRST116') {
                    // "No rows" can be transient right after onboarding creation.
                    if (allowTransientNotFoundRetry && scheduleTransientRetry('project row not yet visible', fetchProject)) {
                        return;
                    }
                    // No rows returned - project doesn't exist (reset + redirect when appropriate)
                    setProject(null);
                    setProjectActivatedState(null);
                    setIsRecoveringActivation(false);
                    setError(msg({ id: 'projectContext.notFound', message: 'Project not found' }));
                    handleRedirect(sessionCandidate);
                } else if (fetchError.code === '42501' || fetchError.message?.includes('permission denied')) {
                    // Permission denied error - likely RLS issue
                    setIsRecoveringActivation(false);
                    setError(msg({ id: 'projectContext.accessDenied', message: 'Access denied' }));
                    console.error('🔍 [ProjectContext] Permission denied - RLS policy issue');
                } else {
                    setIsRecoveringActivation(false);
                    setError(msg({ id: 'projectContext.loadFailure', message: 'Failed to load project' }));
                }
                setIsLoading(false);
                return;
            }

            if (!projectData) {
                if (allowTransientNotFoundRetry && scheduleTransientRetry('empty project response', fetchProject)) {
                    return;
                }
                console.log('🔍 [ProjectContext] No project data found');
                setIsRecoveringActivation(false);
                setError(msg({ id: 'projectContext.notFound', message: 'Project not found' }));
                handleRedirect(sessionCandidate);
                setIsLoading(false);
                return;
            }

            // Validate ownership using the localSession since that's what created the project
            const isOwner = validateOwnership(projectData as ProjectWithRelations, sessionCandidate);

            if (!isOwner) {
                if (allowTransientNotFoundRetry && scheduleTransientRetry('ownership mismatch', fetchProject)) {
                    return;
                }
                console.log('🔍 [ProjectContext] Ownership validation failed');
                console.log('  - Project user_id:', projectData.user_id);
                console.log('  - Local session user_id:', localSession?.user?.id);
                console.log('  - Main session user_id:', session?.user?.id);
                setIsRecoveringActivation(false);
                    setError(msg({ id: 'projectContext.noAccess', message: 'You do not have access to this project' }));
                handleRedirect(sessionCandidate);
                setIsLoading(false);
                return;
            }

            console.log('✅ [ProjectContext] Project fetched and validated successfully');
            console.log('📊 [ProjectContext] Full project data:', projectData);
            console.log('📊 [ProjectContext] Category interests array:', projectData.project_category_interests);
            console.log('📊 [ProjectContext] First category interest:', projectData.project_category_interests?.[0]);
            console.log('📊 [ProjectContext] Nested category in first item:', projectData.project_category_interests?.[0]?.category);
            const projectWithFlag = projectData as ProjectWithRelations & { pending_activation?: boolean };
            const shouldAttemptActivationRecovery = shouldRecoverProjectActivation(projectWithFlag, sessionCandidate);
            const recoveryReason = getActivationRecoveryReason(projectWithFlag, sessionCandidate);
            const isActivationRecoveryPending = shouldAttemptActivationRecovery && recoveryReason !== null;

            setProject(projectWithFlag);
            setProjectActivatedState(isProjectActivated(projectWithFlag));
            setIsRecoveringActivation(isActivationRecoveryPending);
            setError(null);
            resetTransientRetry();

            if (shouldAttemptActivationRecovery && recoveryReason && !activationRecoveryInFlightRef.current.has(projectId)) {
                activationRecoveryInFlightRef.current.add(projectId);
                console.log(`🚀 [ProjectContext] Auto-activating experimental project (${recoveryReason})...`);

                // Activate in background (don't await to avoid blocking UI)
                activateDiscoveryProject(projectId)
                    .then(async () => {
                        console.log('✅ [ProjectContext] Background activation successful');

                        // Keep this idempotent for older rows where flag cleanup might still be pending.
                        if (projectWithFlag.pending_activation) {
                            await supabase
                                .from('project')
                                .update({ pending_activation: false })
                                .eq('id', projectId);
                        }

                        // Refetch project to update UI with new mode
                        fetchProject();
                    })
                    .catch(error => {
                        console.error('❌ [ProjectContext] Background activation failed:', error);
                        setIsRecoveringActivation(false);
                    })
                    .finally(() => {
                        activationRecoveryInFlightRef.current.delete(projectId);
                    });
            }
        } catch (err) {
            console.error('🔍 [ProjectContext] Unexpected error:', err);
            setIsRecoveringActivation(false);
            setError(msg({ id: 'projectContext.loadFailure', message: 'Failed to load project' }));
            handleRedirect(session);
        } finally {
            setIsLoading(false);
        }
    }, [deferProjectAccess, clearTransientRetry, allowTransientNotFoundRetry, projectId, activationRecoverySessionKey, sessionLoading, handleRedirect, resetTransientRetry, scheduleTransientRetry]);

    const validateOwnership = (projectData: ProjectWithRelations, currentSession: Session | null): boolean => {
        if (!currentSession) {
            console.log('🔍 [ProjectContext] No session - ownership validation failed');
            return false;
        }

        // Check if project belongs to the current user
        const isOwner = projectData.user_id === currentSession.user.id;

        console.log('🔍 [ProjectContext] Ownership check:', {
            projectUserId: projectData.user_id,
            sessionUserId: currentSession.user.id,
            isOwner
        });

        return isOwner;
    };

    useEffect(() => {
        if (sessionLoading || !session?.user?.id) {
            return;
        }
        fetchProject();
    }, [fetchProject, sessionLoading, activationRecoverySessionKey]);

    useEffect(() => {
        return () => {
            clearTransientRetry();
        };
    }, [clearTransientRetry]);

    // Set up real-time subscription for project updates
    useEffect(() => {
        if (deferProjectAccess || !projectId || !session?.user?.id) return;

        console.log('🔄 [ProjectContext] Setting up project subscription');

        // Use supabase for discovery projects
        const subscription = supabase
            .channel(`project-context-${projectId}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'project',
                    filter: `id=eq.${projectId}`
                },
                (payload: any) => {
                    console.log('🔄 [ProjectContext] Project updated:', payload);

                    if (payload.eventType === 'UPDATE' && payload.new) {
                        // Check if project was soft-deleted
                        if (payload.new.deleted_at) {
                            console.log('🔄 [ProjectContext] Project soft-deleted, redirecting');
                    setError(msg({ id: 'projectContext.deleted', message: 'Project has been deleted' }));
                            handleRedirect(session);
                            return;
                        }

                        // Merge the updated project data with existing relations
                        // to avoid losing category and keyword data from real-time updates
                        setProject((prev) => {
                            console.log('📊 [ProjectContext] Merging update - prev.project_category_interests:', prev?.project_category_interests);
                            console.log('📊 [ProjectContext] Merging update - payload.new:', payload.new);

                            const merged = {
                                ...payload.new,
                                project_category_interests: prev?.project_category_interests || [],
                                project_keywords: prev?.project_keywords || []
                            } as ProjectWithRelations;

                            console.log('📊 [ProjectContext] Merged result - category_interests:', merged.project_category_interests);
                            return merged;
                        });
                        setProjectActivatedState(isProjectActivated(payload.new as ProjectWithRelations));
                        setIsRecoveringActivation(
                            Boolean(payload.new.pending_activation) && !isProjectActivated(payload.new as ProjectWithRelations)
                        );
                    } else if (payload.eventType === 'DELETE') {
                        console.log('🔄 [ProjectContext] Project deleted, redirecting');
                        setIsRecoveringActivation(false);
                        setError(msg({ id: 'projectContext.deleted', message: 'Project has been deleted' }));
                        handleRedirect(session);
                    }
                }
            )
            .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    // Close the initial fetch-to-subscribe gap and refresh after reconnects.
                    // Settings can then invalidate entitlement state when paused_at changes.
                    void fetchProject();
                }
            });

        return () => {
            console.log('🔄 [ProjectContext] Cleaning up project subscription');
            subscription.unsubscribe();
            supabase.removeChannel(subscription);
        };
    }, [deferProjectAccess, fetchProject, handleRedirect, projectId, session]);

    // Determine project state based on what we know
    const isNewProject = project ? !isProjectActivated(project) : (projectActivatedState === false);
    const isExistingProject = project ? isProjectActivated(project) : (projectActivatedState === true);

    const value: ProjectContextType = {
        project,
        isLoading,
        error,
        isRecoveringActivation,
        isNewProject,
        isExistingProject,
        refetchProject: fetchProject
    };

    return (
        <ProjectContext.Provider value={value}>
            {children}
        </ProjectContext.Provider>
    );
}

export function useProject() {
    const context = useContext(ProjectContext);
    if (context === undefined) {
        throw new Error('useProject must be used within a ProjectProvider');
    }
    return context;
}

// Helper hook to use project in components that might not have provider
export function useProjectSafe() {
    try {
        return useProject();
    } catch {
        return {
            project: null,
            isLoading: false,
            error: null,
            isRecoveringActivation: false,
            isNewProject: false,
            isExistingProject: false,
            refetchProject: async () => {}
        };
    }
}
